import React, { useState } from 'react';
import { GlassCard, GlassBadge, GlassButton } from '@/design-system';
import {
  ChevronDown,
  ChevronRight,
  Target,
  Building2,
  Layers,
  FileCode,
  Edit2,
  Trash2,
  Eye,
  TrendingUp,
  Link2,
} from 'lucide-react';
import type { Objective, KeyResult, OKRTree, OKRTreeNode, OKRLevel, OKRStatusNew } from '@/types';

interface OKRTreeViewProps {
  tree: OKRTree | null;
  onViewDetails: (objective: Objective) => void;
  onEdit: (objective: Objective) => void;
  onDelete: (id: string) => void;
  onStatusChange: (id: string, status: string) => void;
  onProgressUpdate: (keyResult: KeyResult) => void;
  getLevelColor: (level?: OKRLevel) => string;
  getStatusColor: (status: OKRStatusNew) => string;
}

interface TreeNodeProps {
  node: OKRTreeNode;
  level: number;
  onViewDetails: (objective: Objective) => void;
  onEdit: (objective: Objective) => void;
  onDelete: (id: string) => void;
  onStatusChange: (id: string, status: string) => void;
  onProgressUpdate: (keyResult: KeyResult) => void;
  getLevelColor: (level?: OKRLevel) => string;
  getStatusColor: (status: OKRStatusNew) => string;
}

const TreeNode: React.FC<TreeNodeProps> = ({
  node,
  level,
  onViewDetails,
  onEdit,
  onDelete,
  onStatusChange,
  onProgressUpdate,
  getLevelColor,
  getStatusColor,
}) => {
  const [isExpanded, setIsExpanded] = useState(level < 2);
  const [showKeyResults, setShowKeyResults] = useState(false);

  const hasChildren = node.alignedChildren && node.alignedChildren.length > 0;
  const hasKeyResults = node.keyResults && node.keyResults.length > 0;
  const progress = node.overallProgress ?? 0;

  const getLevelIcon = (okrLevel?: OKRLevel) => {
    switch (okrLevel) {
      case 'IS':
        return <Layers className="w-4 h-4" />;
      case 'PROGRAM':
        return <Building2 className="w-4 h-4" />;
      case 'EPIC':
        return <FileCode className="w-4 h-4" />;
      default:
        return <Target className="w-4 h-4" />;
    }
  };

  const getProgressColor = (p: number | null | undefined) => {
    if (p === null || p === undefined) return 'bg-gray-500';
    if (p >= 75) return 'bg-green-500';
    if (p >= 50) return 'bg-yellow-500';
    if (p >= 25) return 'bg-orange-500';
    return 'bg-red-500';
  };

  return (
    <div className="relative">
      {/* Connection line from parent */}
      {level > 0 && (
        <div
          className="absolute left-0 top-0 w-6 h-full border-l-2 border-b-2 border-[var(--border-primary)] rounded-bl-lg"
          style={{ marginLeft: `${(level - 1) * 32 + 16}px`, height: '24px' }}
        />
      )}

      <div style={{ marginLeft: `${level * 32}px` }}>
        {/* Node Card */}
        <div className="bg-[var(--bg-tertiary)]/50 border border-[var(--border-primary)] rounded-lg p-3 mb-2">
          <div className="flex items-center gap-3">
            {/* Expand/Collapse for children */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className={`p-1 rounded hover:bg-[var(--bg-hover)]/50 transition-colors ${
                !hasChildren ? 'invisible' : ''
              }`}
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              )}
            </button>

            {/* Level Icon */}
            <div
              className={`p-1.5 rounded-md bg-${getLevelColor(node.level)}-500/20 text-${getLevelColor(node.level)}-400`}
            >
              {getLevelIcon(node.level)}
            </div>

            {/* Title and badges */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span
                  onClick={() => onViewDetails(node)}
                  className="text-sm font-medium text-foreground hover:text-blue-400 cursor-pointer transition-colors"
                >
                  {node.title}
                </span>
                <GlassBadge variant={getStatusColor(node.status) as any} className="text-xs">
                  {node.status}
                </GlassBadge>
              </div>
              <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                <span>Q{node.quarter} FY{node.fiscalYear.toString().slice(-2)}</span>
                {node.entityName && <span>{node.entityName}</span>}
                {hasKeyResults && (
                  <button
                    onClick={() => setShowKeyResults(!showKeyResults)}
                    className="text-blue-400 hover:text-blue-300"
                  >
                    {node.keyResults?.length} KR{node.keyResults?.length !== 1 ? 's' : ''}
                  </button>
                )}
                {hasChildren && (
                  <span className="flex items-center gap-1">
                    <Link2 className="w-3 h-3" />
                    {node.alignedChildren.length} aligned
                  </span>
                )}
              </div>
            </div>

            {/* Progress */}
            <div className="flex items-center gap-2">
              <div className="text-right">
                <div className="text-sm font-semibold text-foreground">{Math.round(progress)}%</div>
                <div className="w-16 h-1.5 bg-[var(--bg-tertiary)]/50 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${getProgressColor(progress)} transition-all duration-300`}
                    style={{ width: `${Math.min(progress, 100)}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => onViewDetails(node)}
                className="p-1.5 rounded hover:bg-[var(--bg-hover)]/50 text-muted-foreground hover:text-foreground transition-colors"
                title="View Details"
              >
                <Eye className="w-4 h-4" />
              </button>
              <button
                onClick={() => onEdit(node)}
                className="p-1.5 rounded hover:bg-[var(--bg-hover)]/50 text-muted-foreground hover:text-foreground transition-colors"
                title="Edit"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDelete(node.id)}
                className="p-1.5 rounded hover:bg-red-500/20 text-muted-foreground hover:text-red-400 transition-colors"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Key Results (expanded) */}
          {showKeyResults && hasKeyResults && (
            <div className="mt-3 pt-3 border-t border-[var(--border-primary)] space-y-2">
              {node.keyResults?.map((kr) => (
                <div
                  key={kr.id}
                  className="flex items-center justify-between p-2 bg-[var(--bg-tertiary)]/50 rounded"
                >
                  <div className="flex-1 min-w-0 mr-3">
                    <p className="text-xs text-muted-foreground">{kr.description}</p>
                    <div className="text-xs text-muted-foreground mt-1">
                      {kr.currentValue ?? kr.startValue} / {kr.targetValue} {kr.unit || ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-xs text-foreground">{Math.round(kr.progress ?? 0)}%</div>
                    <button
                      onClick={() => onProgressUpdate(kr)}
                      className="p-1 rounded hover:bg-[var(--bg-hover)]/50 text-muted-foreground hover:text-foreground"
                    >
                      <TrendingUp className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Children */}
        {isExpanded && hasChildren && (
          <div className="relative">
            {/* Vertical line connecting children */}
            <div
              className="absolute left-4 top-0 bottom-4 w-0.5 bg-[var(--border-primary)]"
              style={{ marginLeft: '14px' }}
            />
            {node.alignedChildren.map((child, index) => (
              <TreeNode
                key={child.id}
                node={child}
                level={level + 1}
                onViewDetails={onViewDetails}
                onEdit={onEdit}
                onDelete={onDelete}
                onStatusChange={onStatusChange}
                onProgressUpdate={onProgressUpdate}
                getLevelColor={getLevelColor}
                getStatusColor={getStatusColor}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const OKRTreeView: React.FC<OKRTreeViewProps> = ({
  tree,
  onViewDetails,
  onEdit,
  onDelete,
  onStatusChange,
  onProgressUpdate,
  getLevelColor,
  getStatusColor,
}) => {
  if (!tree) {
    return (
      <GlassCard className="p-12 text-center">
        <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">No tree data available</h3>
        <p className="text-muted-foreground">
          Create objectives and align them to build your OKR hierarchy.
        </p>
      </GlassCard>
    );
  }

  const hasISObjectives = tree.isObjectives && tree.isObjectives.length > 0;
  const hasProgramObjectives = tree.programObjectives && tree.programObjectives.length > 0;
  const hasEpicObjectives = tree.epicObjectives && tree.epicObjectives.length > 0;
  const hasAnyObjectives = hasISObjectives || hasProgramObjectives || hasEpicObjectives;

  if (!hasAnyObjectives) {
    return (
      <GlassCard className="p-12 text-center">
        <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">No objectives found</h3>
        <p className="text-muted-foreground">
          Create objectives to see them displayed in the tree view.
        </p>
      </GlassCard>
    );
  }

  return (
    <div className="space-y-6">
      {/* IS Level Objectives */}
      {hasISObjectives && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-md bg-blue-500/20">
              <Layers className="w-4 h-4 text-blue-400" />
            </div>
            <h3 className="text-sm font-medium text-muted-foreground">
              IS Level Objectives ({tree.isObjectives.length})
            </h3>
          </div>
          <div className="space-y-2">
            {tree.isObjectives.map((node) => (
              <TreeNode
                key={node.id}
                node={node}
                level={0}
                onViewDetails={onViewDetails}
                onEdit={onEdit}
                onDelete={onDelete}
                onStatusChange={onStatusChange}
                onProgressUpdate={onProgressUpdate}
                getLevelColor={getLevelColor}
                getStatusColor={getStatusColor}
              />
            ))}
          </div>
        </div>
      )}

      {/* Program Level Objectives (unaligned to IS) */}
      {hasProgramObjectives && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-md bg-blue-500/20">
              <Building2 className="w-4 h-4 text-blue-400" />
            </div>
            <h3 className="text-sm font-medium text-muted-foreground">
              Program Level Objectives ({tree.programObjectives.length})
            </h3>
          </div>
          <div className="space-y-2">
            {tree.programObjectives.map((node) => (
              <TreeNode
                key={node.id}
                node={node}
                level={0}
                onViewDetails={onViewDetails}
                onEdit={onEdit}
                onDelete={onDelete}
                onStatusChange={onStatusChange}
                onProgressUpdate={onProgressUpdate}
                getLevelColor={getLevelColor}
                getStatusColor={getStatusColor}
              />
            ))}
          </div>
        </div>
      )}

      {/* EPIC Level Objectives (unaligned to Program or IS) */}
      {hasEpicObjectives && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-md bg-green-500/20">
              <FileCode className="w-4 h-4 text-green-400" />
            </div>
            <h3 className="text-sm font-medium text-muted-foreground">
              EPIC Level Objectives ({tree.epicObjectives.length})
            </h3>
          </div>
          <div className="space-y-2">
            {tree.epicObjectives.map((node) => (
              <TreeNode
                key={node.id}
                node={node}
                level={0}
                onViewDetails={onViewDetails}
                onEdit={onEdit}
                onDelete={onDelete}
                onStatusChange={onStatusChange}
                onProgressUpdate={onProgressUpdate}
                getLevelColor={getLevelColor}
                getStatusColor={getStatusColor}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OKRTreeView;
